import { useStore } from 'react-redux';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { LogLevel } from 'features/analytics/enums';
import { useAppDispatch } from 'features/app/hooks';
import type { RootState } from 'features/app/store/store';
import { findLatestHearingByPhoneNumber } from 'features/call/call-hearing/helpers';
import {
  pendingCancelById,
  pendingCancelByPhoneNumber,
  selectHasOtherActiveHearings,
} from 'features/call/call-hearing/store';
import { handleError } from 'features/notification/store';
import { takeInFlightAddParticipant } from 'features/voice-meeting/helpers';
import { useVoiceMeetingSession } from 'features/voice-meeting/hooks';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import { voiceSessionIdSelector } from 'features/voice-session/store';

type CancelHearingArgs = {
  id?: string;
  phoneNumber: string;
};

export const useHearingCancel = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { disconnectParticipants } = useVoiceMeetingSession();

  const findHearingForCancel = (phoneNumber: string, knownId?: string) => {
    const hearings = store.getState().callHearing.hearings;

    if (knownId) {
      const byId = hearings.find((hearing) => hearing.id === knownId);

      if (byId) {
        return byId;
      }
    }

    return findLatestHearingByPhoneNumber(hearings, phoneNumber);
  };

  const shouldSkipLateCleanup = (phoneNumber: string, knownId?: string) => {
    const hearing = findHearingForCancel(phoneNumber, knownId);

    return (
      hearing?.teardownIntent !== 'cancel' &&
      hearing?.lastTeardownReason !== 'forced'
    );
  };

  const attachLateCleanup = (phoneNumber: string, knownId?: string) => {
    const pending = takeInFlightAddParticipant(phoneNumber);

    if (!pending) {
      console.log('HearingDebug: late-cleanup skip (no in-flight)', {
        phoneNumber,
        knownId: knownId || null,
      });
      return;
    }

    console.log('HearingDebug: late-cleanup attached', {
      phoneNumber,
      knownId: knownId || null,
      sessionId: pending.sessionId || null,
    });

    const settle = async () => {
      let resolvedId: string | undefined;

      try {
        const participant = await pending.promise;
        resolvedId = participant?.id;
      } catch {
        console.log('HearingDebug: late-cleanup no-op (in-flight rejected)', {
          phoneNumber,
        });
        return;
      }

      // Cancel-with-id already called disconnectParticipants(knownId). Skip
      // duplicate delete when the in-flight promise resolves to that same id.
      // Cancel-before-id passes knownId=undefined, so a resolved id always
      // proceeds to deleteParticipant.
      if (!resolvedId || resolvedId === knownId) {
        console.log('HearingDebug: late-cleanup no-op (no new id)', {
          phoneNumber,
          resolvedId: resolvedId || null,
          knownId: knownId || null,
        });
        return;
      }

      // Skip cleanup if cancellation is no longer active and teardown was not forced.
      if (shouldSkipLateCleanup(phoneNumber, knownId)) {
        console.log('HearingDebug: late-cleanup skip (not cancel/forced)', {
          phoneNumber,
          resolvedId,
        });
        return;
      }

      // Prefer the session id captured with the in-flight add — Redux/service
      // may already be cleared by a forced last-hearing session teardown.
      const sessionId =
        pending.sessionId || voiceSessionIdSelector(store.getState());

      if (!sessionId) {
        console.log('HearingDebug: late-cleanup skip (no sessionId)', {
          phoneNumber,
          resolvedId,
          capturedSessionId: pending.sessionId || null,
        });
        return;
      }

      try {
        console.log('HearingDebug: late-cleanup deleteParticipant', {
          sessionId,
          participantId: resolvedId,
        });
        await VoiceSessionDaoService.deleteParticipant({
          sessionId,
          participantId: resolvedId,
        });
      } catch (error) {
        console.log('HearingDebug: late-cleanup deleteParticipant failed', {
          sessionId,
          participantId: resolvedId,
          error,
        });
        dispatch(
          handleError({
            error,
            methodName: 'useHearingCancel.attachLateCleanup',
            disableUserNotification: true,
          })
        );

        return;
      }

      const hasOthers = selectHasOtherActiveHearings(
        store.getState(),
        resolvedId,
        phoneNumber
      );

      if (!hasOthers) {
        console.log('HearingDebug: late-cleanup finishAndDeleteVoiceSession', {
          phoneNumber,
          resolvedId,
        });
        VoiceMeetingEventBus.finishAndDeleteVoiceSession();
      } else {
        console.log('HearingDebug: late-cleanup keep session (others active)', {
          phoneNumber,
          resolvedId,
        });
      }
    };

    void settle();
  };

  const cancelHearing = async ({ id, phoneNumber }: CancelHearingArgs) => {
    console.log('HearingDebug: cancelHearing', {
      id: id || null,
      phoneNumber,
      hasId: Boolean(id),
    });

    if (id) {
      dispatch(pendingCancelById({ id }));
    } else {
      dispatch(pendingCancelByPhoneNumber({ phoneNumber }));
    }

    dispatch(
      sendAnalyticsInfo({
        Method: 'useHearingCancel.cancelHearing',
        Level: LogLevel.INFO,
        Message: `Cancel intent set for hearing id=${id || 'none'} phone=${phoneNumber}`,
      })
    );

    attachLateCleanup(phoneNumber, id);

    if (id) {
      try {
        console.log('HearingDebug: cancelHearing disconnectParticipants', {
          id,
        });
        await disconnectParticipants([id]);
      } catch (error) {
        console.log(
          'HearingDebug: cancelHearing disconnectParticipants failed',
          {
            id,
            error,
          }
        );
        dispatch(
          handleError({
            error,
            methodName: 'useHearingCancel.cancelHearing',
            disableUserNotification: true,
          })
        );
      }
    }

    // If no id exists yet, the teardown watcher forces local teardown at the
    // deadline. Late cleanup handles backend cleanup once addParticipant or
    // createNewVoiceSession resolves.
    //
    // Cancel-with-id session finishing waits for $disconnected (VRS canDelete)
    // or a watcher force. Finishing immediately would run
    // disconnectAllHearings and clear cancel UI state before the acknowledgement.
  };

  return { cancelHearing };
};
